/**
 * Cart Actions
 * 
 * Business logic for cart operations.
 * These functions handle all cart item manipulation and checkout creation.
 * 
 * PERFORMANCE: Uses dynamic imports for Shopify checkout to keep
 * the heavy Shopify/Zod bundle out of the initial load.
 */

import { CartState, CartStateMutations } from './types';
import { logError } from '@/lib/error-handling';
import { createStructuredError, ErrorCode, RecoveryAction } from '@/types/errors';

// Import only the type for TypeScript (erased at runtime)
import type { CartItem } from '@/lib/shopify';

/**
 * Creates cart action handlers
 * 
 * These functions encapsulate business logic and use state setters
 * to update the store. This separation makes testing easier.
 */
export const createCartActions = (
  get: () => CartState,
  mutations: CartStateMutations
) => {
  /**
   * Add item to cart
   * If item exists, increases quantity; otherwise adds new item
   */
  const addItem = (item: CartItem): void => {
    const { items } = get();
    const existingItem = items.find(i => i.variantId === item.variantId);
    
    if (existingItem) {
      // Item exists - increase quantity
      const updatedItems = items.map(i =>
        i.variantId === item.variantId
          ? { ...i, quantity: i.quantity + item.quantity }
          : i
      );
      mutations.setItems(updatedItems);
    } else {
      // New item - add to cart
      mutations.setItems([...items, item]);
    }
    
    // Reset any previous checkout errors
    mutations.resetCheckoutState();
  };

  /**
   * Update quantity of cart item
   * If quantity is 0 or less, removes the item
   */
  const updateQuantity = (variantId: string, quantity: number): void => {
    const { items } = get();
    
    if (quantity <= 0) {
      // Remove item if quantity is 0 or negative
      removeItem(variantId);
      return;
    }
    
    const updatedItems = items.map(item =>
      item.variantId === variantId
        ? { ...item, quantity }
        : item
    );
    
    mutations.setItems(updatedItems);
    mutations.resetCheckoutState();
  };

  /**
   * Remove item from cart
   */
  const removeItem = (variantId: string): void => {
    const { items } = get();
    const updatedItems = items.filter(item => item.variantId !== variantId);
    
    mutations.setItems(updatedItems);
    mutations.resetCheckoutState();
  };

  /**
   * Clear all items from cart
   * Also resets cart ID and checkout URL
   */
  const clearCart = (): void => {
    mutations.setItems([]);
    mutations.setCartId(null);
    mutations.setCheckoutUrl(null);
    mutations.resetCheckoutState();
  };

  /**
   * Create Shopify checkout session
   * 
   * Handles the async operation of creating a checkout,
   * including loading states and error handling with discriminated unions.
   * 
   * Uses dynamic import to keep Shopify bundle out of initial load.
   */
  const createCheckout = async (): Promise<void> => {
    const { items } = get();
    
    // Validation
    if (items.length === 0) {
      const error = createStructuredError(
        'Cannot create checkout with empty cart',
        ErrorCode.CART_ERROR,
        RecoveryAction.GO_BACK
      );
      mutations.setCheckoutError(error);
      return;
    }

    mutations.setCheckoutLoading();

    try {
      // Dynamic import - Shopify bundle only loads when user actually checks out
      const { createStorefrontCheckout } = await import('@/lib/shopify');
      const checkoutUrl = await createStorefrontCheckout(items);
      
      mutations.setCheckoutUrl(checkoutUrl);
      mutations.setCheckoutSuccess();
    } catch (error) {
      logError('Create Checkout', error);
      
      const structuredError = createStructuredError(
        error,
        ErrorCode.CHECKOUT_ERROR,
        RecoveryAction.RETRY
      );
      
      mutations.setCheckoutError(structuredError);
      
      // Re-throw so calling code can handle it
      throw error;
    }
  };

  return {
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    createCheckout,
  };
};

/**
 * Validate cart item before adding
 * Ensures item has required fields
 */
export const validateCartItem = (item: CartItem): { isValid: boolean; error?: string } => {
  if (!item.variantId) {
    return { isValid: false, error: 'Variant ID is required' };
  }
  
  if (!item.product) {
    return { isValid: false, error: 'Product data is required' };
  }
  
  if (item.quantity <= 0) {
    return { isValid: false, error: 'Quantity must be greater than 0' };
  }
  
  if (!item.price || !item.price.amount) {
    return { isValid: false, error: 'Price information is required' };
  }
  
  return { isValid: true };
};
